/**
 * FieldRenderer — one row of the shared form.
 *
 * Responsibilities:
 *   - Pick the correct input component for `field.field_type`.
 *   - Render the label (with required asterisk + disabled marker), description
 *     (Step 8 — visible in every mode including preview), per-field decline
 *     comment slot, and revision marker.
 *   - Show blocking error (when present) and soft-warning blocks with inline
 *     clinical-justification textareas (data-entry / review modes).
 *
 * Touched-state gating is handled by the parent `FormRenderer`: it decides
 * whether to pass an `error` to this component based on `touched` /
 * `submitAttempted` (Step 4). FieldRenderer is purely presentational.
 */
import type React from 'react'
import { AlertCircle, AlertTriangle, CheckCircle2, Info } from 'lucide-react'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { cn } from '@/lib/utils/cn'
import type {
  FieldDefinition,
} from '@/features/form-builder/api/form-builder-api'
import type { SoftWarning } from '@bluedatalake/form-engine'
import type { FileUploadContext, InputProps } from './input-props'

import { TextInput }            from './inputs/text-input'
import { TextareaInput }        from './inputs/textarea-input'
import { NumberInput }          from './inputs/number-input'
import { DateInput }            from './inputs/date-input'
import { TimeInput }            from './inputs/time-input'
import { DateTimeInput }        from './inputs/datetime-input'
import { BooleanInput }         from './inputs/boolean-input'
import { SingleSelectInput }    from './inputs/single-select-input'
import { MultiSelectInput }     from './inputs/multi-select-input'
import { CalculatedDisplay }    from './inputs/calculated-display'
import { FileInput, ImageInput } from './inputs/file-input'
import { RepeatableArrayInput } from './inputs/repeatable-array-input'

const INPUT_BY_TYPE: Record<string, (p: InputProps) => React.JSX.Element> = {
  text:             TextInput,
  textarea:         TextareaInput,
  number:           NumberInput,
  date:             DateInput,
  time:             TimeInput,
  datetime:         DateTimeInput,
  boolean:          BooleanInput,
  single_select:    SingleSelectInput,
  multi_select:     MultiSelectInput,
  calculated:       CalculatedDisplay,
  file:             FileInput,
  image:            ImageInput,
  repeatable_array: RepeatableArrayInput,
}

export interface FieldRendererProps {
  field: FieldDefinition
  value: unknown
  onChange: (val: unknown) => void
  onBlur: () => void
  /** From the condition engine. */
  required: boolean
  enabled: boolean
  filteredOptions?: string[]
  disabled?: boolean
  error?: string
  warnings: SoftWarning[]
  justifications: Record<string, string>
  onJustificationChange: (jKey: string, val: string) => void
  uploadCtx?: FileUploadContext
  /** Whether the field is flagged for revision because the form version was bumped. */
  needsRevision?: boolean
  /** Whether there are open decline comments on this field. */
  hasDeclineComments?: boolean
  /** Slot for the field-level comments thread (rendered below the input). */
  commentSlot?: React.ReactNode
  /**
   * Slot for prior-justification history (read-only / approver view). Called
   * once per warning; consumer renders nothing when there's no history to show.
   */
  renderJustificationHistory?: (fieldKey: string, ruleId: string) => React.ReactNode
  /** Suppress the description block (e.g., editor side-by-side already shows it). */
  hideDescription?: boolean
}

export function FieldRenderer({
  field,
  value, onChange, onBlur,
  required, enabled, filteredOptions,
  disabled, error, warnings,
  justifications, onJustificationChange,
  uploadCtx,
  needsRevision, hasDeclineComments, commentSlot,
  renderJustificationHistory,
  hideDescription,
}: FieldRendererProps) {
  const Input = INPUT_BY_TYPE[field.field_type] ?? TextInput
  const label = field.label.fa || field.field_key
  const description = field.description?.fa

  const wrapperCls = cn(
    'group/field space-y-1.5',
    hasDeclineComments && 'border-s-2 border-red-500 ps-3',
    needsRevision && 'border-s-2 border-yellow-400 ps-3',
  )

  return (
    <div className={wrapperCls}>
      <Label className={cn('flex items-center gap-1.5', error && 'text-destructive')}>
        <span>{label}</span>
        {required && <span className="text-red-400" aria-hidden>*</span>}
        {!enabled && (
          <span className="text-xs text-muted-foreground font-normal">(غیرفعال)</span>
        )}
        {needsRevision && (
          <span title="این فیلد نیاز به بازبینی دارد چون فرم به‌روزرسانی شده است">
            <AlertTriangle className="w-3.5 h-3.5 text-yellow-400" />
          </span>
        )}
      </Label>

      {!hideDescription && description && (
        <p className="flex items-start gap-1.5 rounded-md border-s-2 border-primary/40 bg-muted/40 px-2 py-1 text-[13px] leading-relaxed text-foreground/75">
          <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary/70" aria-hidden />
          <span>{description}</span>
        </p>
      )}

      <Input
        field={field}
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        disabled={disabled}
        filteredOptions={filteredOptions}
        invalid={!!error}
        uploadCtx={uploadCtx}
      />

      {error && (
        <p className="text-xs text-destructive flex items-center gap-1">
          <AlertCircle className="w-3 h-3" />
          {error}
        </p>
      )}

      {!error && warnings.map((w) => {
        const jKey = `${field.field_key}_${w.ruleId}`
        const justified = (justifications[jKey] ?? '').trim().length > 0
        return (
          <div
            key={w.ruleId}
            className="rounded-md border border-yellow-500/30 bg-yellow-500/10 p-2 space-y-1.5"
          >
            <p className="text-xs text-yellow-400 flex items-start gap-1.5">
              <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
              <span className="flex-1">{w.message}</span>
              {justified && <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 shrink-0" />}
            </p>
            <Textarea
              value={justifications[jKey] ?? ''}
              onChange={(e) => onJustificationChange(jKey, e.target.value)}
              placeholder="توجیه بالینی برای ادامه ارسال..."
              rows={2}
              disabled={disabled}
              className="text-xs resize-none bg-background"
            />
            {renderJustificationHistory?.(field.field_key, w.ruleId)}
          </div>
        )
      })}

      {commentSlot}
    </div>
  )
}
